import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CardActions,
    CardHeader,
    Container,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Link,
    Stack,
    styled,
    Typography,
    Card as MuiCard,
    CardContent,
    CssBaseline,
} from '@mui/material';
import { alpha, createTheme, ThemeProvider } from '@mui/material/styles';
import { orange } from '@mui/material/colors';
import {
    ChevronLeft,
    Close,
    ExpandMore,
    ForumOutlined,
    HeadsetMicOutlined,
    ReportProblemOutlined,
    ZoomIn,
} from '@mui/icons-material';
import smsUserGuide from '../../assets/help/SMS User Guide 20260824.png';
import smsErrorGuide from '../../assets/help/SMS Error Guide 20260709.png';

const ACCENT = orange[800];

const theme = createTheme({
    palette: {
        mode: 'light',
    },
    typography: {
        fontFamily: 'Trebuchet MS',
    },
});

const GUIDES = [
    {
        id: 'sms-user-guide',
        title: 'SMS User Guide',
        description: 'Step-by-step instructions for using the SMS service.',
        icon: ForumOutlined,
        src: smsUserGuide,
        alt: 'SMS User Guide',
    },
    {
        id: 'sms-error-guide',
        title: 'SMS Error Guide',
        description: 'Common error messages and how to resolve them.',
        icon: ReportProblemOutlined,
        src: smsErrorGuide,
        alt: 'SMS Error Guide',
    },
] as const;

type Guide = (typeof GUIDES)[number];

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    border: 'none',
    fontFamily: 'inherit',
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
        width: 700,
    },
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const HelpContainer = styled(Stack)(({ theme }) => ({
    minHeight: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
    padding: theme.spacing(2),
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'fixed',
        zIndex: -1,
        inset: 0,
        backgroundImage: 'url(https://wordpress.idea4industry.com/wp-content/uploads/AdobeStock_443793149-speed-blue-1536x922.jpg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor:
            'radial-gradient(ellipse at 50% 50%, hsl(211, 89.70%, 7.60%), hsl(0, 11.10%, 10.60%))',
        ...theme.applyStyles('dark', {
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundImage: 'url(https://wordpress.idea4industry.com/wp-content/uploads/AdobeStock_443793149-speed-blue-1536x922.jpg)',
            backgroundColor:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

const FooterLink = styled(Link)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.625),
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: ACCENT,
    textDecoration: 'none',
    transition: theme.transitions.create(['color', 'opacity'], {
        duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
        color: orange[900],
        opacity: 0.9,
    },
    '& .MuiSvgIcon-root': {
        fontSize: '0.95rem',
    },
}));

const FooterNav = styled('nav')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.25),
}));

const GuideImageButton = styled('button')(({ theme }) => ({
    display: 'block',
    width: '100%',
    margin: 0,
    padding: theme.spacing(0, 2, 2),
    border: 'none',
    background: 'transparent',
    cursor: 'zoom-in',
    position: 'relative',
    '&:hover .guide-zoom-hint': {
        opacity: 1,
        transform: 'translateY(0)',
    },
    '&:hover .guide-image-frame': {
        transform: 'scale(1.01)',
        boxShadow: `0 16px 32px ${alpha(theme.palette.common.black, 0.14)}`,
    },
    '&:focus-visible': {
        outline: `3px solid ${alpha(theme.palette.primary.main, 0.45)}`,
        outlineOffset: 4,
        borderRadius: 16,
    },
}));

function GuideHeader({ guide }: { guide: Guide }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '72px minmax(0, 1fr)',
                columnGap: 1.5,
                gridTemplateRows: 'auto auto',
                alignItems: 'center',
                width: '100%',
                minWidth: 0,
                pr: 1,
            }}
        >
            <Box
                sx={{
                    gridRow: '1 / -1',
                    alignSelf: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    flexShrink: 0,
                    color: ACCENT,
                    bgcolor: alpha(orange[500], 0.12),
                    '& .MuiSvgIcon-root': {
                        fontSize: '2.5rem',
                    },
                }}
            >
                <guide.icon />
            </Box>
            <Typography
                variant="subtitle1"
                sx={{
                    gridColumn: 2,
                    gridRow: 1,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    letterSpacing: '-0.01em',
                }}
            >
                {guide.title}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    mt: 0.25,
                    lineHeight: 1.45,
                }}
            >
                {guide.description}
            </Typography>
        </Box>
    );
}

function GuideImage({
    guide,
    onExpand,
}: {
    guide: Guide;
    onExpand: (guide: Guide) => void;
}) {
    return (
        <GuideImageButton
            type="button"
            aria-label={`View ${guide.title} full size`}
            onClick={() => onExpand(guide)}
            style={{ padding: 0 }}
        >
            <Box
                className="guide-image-frame"
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 1.5,
                    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.65)}`,
                    bgcolor: 'background.paper',
                    boxShadow: (theme) => `0 10px 28px ${alpha(theme.palette.common.black, 0.1)}`,
                    transition: (theme) => theme.transitions.create(['transform', 'box-shadow'], {
                        duration: theme.transitions.duration.standard,
                    }),
                }}
            >
                <Box
                    component="img"
                    src={guide.src}
                    alt={guide.alt}
                    loading="lazy"
                    sx={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <Box
                    className="guide-zoom-hint"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.9,
                        borderRadius: 999,
                        bgcolor: alpha('#0f172a', 0.78),
                        color: 'common.white',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        opacity: 0.92,
                        transform: 'translateY(-4px)',
                        transition: 'opacity 0.2s ease, transform 0.2s ease',
                        backdropFilter: 'blur(8px)',
                        boxShadow: `0 8px 20px ${alpha('#000', 0.22)}`,
                        zIndex: 1,
                    }}
                >
                    <ZoomIn fontSize="small" />
                    Tap to enlarge
                </Box>
            </Box>
        </GuideImageButton>
    );
}

export default function Help() {
    const [expandedGuide, setExpandedGuide] = React.useState<Guide | null>(null);
    const [expandedAccordion, setExpandedAccordion] = React.useState<string | false>(false);

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="md">
                <CssBaseline enableColorScheme />
                <HelpContainer>
                    <Card variant="outlined" sx={{ borderRadius: 1.25 }}>
                        <CardHeader
                            sx={{ pt: 3, pb: 1.5, px: { xs: 2.5, sm: 4 } }}
                            title={
                                <Stack direction="column" alignItems="center" justifyContent="center" fontFamily="inherit" spacing={1.5}>
                                    <Box
                                        component="img"
                                        src="https://idea4industry.com/wp-content/uploads/cropped-IDEA-favicon-192x192.png"
                                        alt="IDEA Data Whispers"
                                        sx={{ width: 56, height: 56 }}
                                    />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontFamily: 'inherit',
                                            maxWidth: '90%',
                                            textAlign: 'center',
                                            fontSize: 'clamp(1.3rem, 10vw, 1.5rem)',
                                        }}
                                    >
                                        <Link href="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                                            IDEA Data Whispers
                                        </Link>
                                    </Typography>
                                </Stack>
                            }
                        />
                        <Divider sx={{ mx: { xs: 2.5, sm: 4 } }} />
                        <CardContent
                            sx={{
                                overflow: 'visible',
                                px: { xs: 2.5, sm: 4 },
                                pt: 2.5,
                                pb: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: alpha('#f4f6f8', 0.95),
                                    borderRadius: 2,
                                    px: { xs: 2, sm: 2.5 },
                                    py: { xs: 2.25, sm: 2.75 },
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                    justifyContent="space-between"
                                    sx={{ mb: 2.5 }}
                                >
                                    <Box sx={{ minWidth: 0, pr: { sm: 1 } }}>
                                        <Typography
                                            component="h1"
                                            variant="h4"
                                            sx={{
                                                fontFamily: 'inherit',
                                                fontWeight: 700,
                                                fontSize: 'clamp(1.5rem, 6vw, 1.75rem)',
                                                letterSpacing: '-0.02em',
                                                lineHeight: 1.2,
                                                color: 'text.primary',
                                            }}
                                        >
                                            Help Center
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.75, fontSize: '0.9rem', lineHeight: 1.55 }}
                                        >
                                            Find user guides and troubleshooting information to help you get the most out of the SMS service.
                                        </Typography>
                                    </Box>
                                    <Button
                                        href="/login"
                                        variant="contained"
                                        size="small"
                                        startIcon={<ChevronLeft />}
                                        sx={{
                                            alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                                            flexShrink: 0,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderRadius: 999,
                                            px: 2,
                                            py: 0.85,
                                            whiteSpace: 'nowrap',
                                            bgcolor: ACCENT,
                                            color: 'common.white',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: orange[900],
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        Back to Sign In
                                    </Button>
                                </Stack>

                                <Stack id="guides-collapsible" spacing={1.5}>
                                    {GUIDES.map((guide) => (
                                        <Box
                                            key={guide.id}
                                            sx={{
                                                border: '1px solid #D1D5DB',
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                bgcolor: '#fff',
                                            }}
                                        >
                                            <Accordion
                                                id={guide.id}
                                                expanded={expandedAccordion === guide.id}
                                                onChange={(_, isExpanded) => setExpandedAccordion(isExpanded ? guide.id : false)}
                                                disableGutters
                                                elevation={0}
                                                square
                                                sx={{
                                                    scrollMarginTop: 2,
                                                    bgcolor: 'transparent',
                                                    boxShadow: 'none',
                                                    '&::before': { display: 'none' },
                                                    '&.Mui-expanded': {
                                                        margin: 0,
                                                    },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMore sx={{ color: ACCENT, fontSize: '2rem' }} />}
                                                    aria-controls={`${guide.id}-content`}
                                                    id={`${guide.id}-header`}
                                                    sx={{
                                                        px: { xs: 1.75, sm: 2 },
                                                        py: 0.25,
                                                        minHeight: 88,
                                                        alignItems: 'center',
                                                        '& .MuiAccordionSummary-content': {
                                                            my: 1.25,
                                                            alignItems: 'center',
                                                        },
                                                    }}
                                                >
                                                    <GuideHeader guide={guide} />
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ px: { xs: 1.75, sm: 2 }, pt: 0, pb: 2 }}>
                                                    <GuideImage guide={guide} onExpand={setExpandedGuide} />
                                                </AccordionDetails>
                                            </Accordion>
                                        </Box>
                                    ))}
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                    justifyContent="center"
                                    sx={{ mt: 3 }}
                                >
                                    <HeadsetMicOutlined sx={{ color: 'text.secondary', fontSize: 22, flexShrink: 0, mt: 0.15 }} />
                                    <Stack spacing={0.5} alignItems="flex-start">
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                            Still need help? Our team is here for you.
                                        </Typography>
                                        <Link
                                            href="mailto:ideacustomersuccess@idea4industry.com"
                                            underline="hover"
                                            sx={{
                                                color: ACCENT,
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            ideacustomersuccess@idea4industry.com
                                        </Link>
                                    </Stack>
                                </Stack>
                            </Box>
                        </CardContent>
                        <Divider sx={{ mx: { xs: 2.5, sm: 4 }, opacity: 0.55 }} />
                        <CardActions sx={{ justifyContent: 'center', flexDirection: 'column', pt: 1.5, pb: 3, px: { xs: 2.5, sm: 4 }, gap: 1.25 }}>
                            <FooterNav aria-label="Legal and support">
                                <FooterLink
                                    href="https://idea4industry.com/privacy-policy/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Privacy Policy
                                </FooterLink>
                                <Typography component="span" color="text.secondary" sx={{ fontSize: '0.8125rem', opacity: 0.5 }}>
                                    |
                                </Typography>
                                <FooterLink href="/login" aria-label="Return to Sign In page">
                                    Back to Sign In
                                </FooterLink>
                            </FooterNav>
                            <Typography
                                id="copyright-text"
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontFamily: 'inherit', textAlign: 'center', width: '100%', fontSize: '0.75rem', opacity: 0.85 }}
                            >
                                {`© ${new Date().getFullYear()}, All rights reserved by IDEA`}
                            </Typography>
                        </CardActions>
                    </Card>
                </HelpContainer>

                <Dialog
                    open={expandedGuide !== null}
                    onClose={() => setExpandedGuide(null)}
                    maxWidth="lg"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: { borderRadius: 2, overflow: 'hidden' },
                        },
                    }}
                >
                    {expandedGuide && (
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    {expandedGuide.title}
                                </Typography>
                                <IconButton aria-label="Close enlarged guide" onClick={() => setExpandedGuide(null)}>
                                    <Close />
                                </IconButton>
                            </Stack>
                            <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                <Box
                                    component="img"
                                    src={expandedGuide.src}
                                    alt={expandedGuide.alt}
                                    sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 1 }}
                                />
                            </DialogContent>
                        </>
                    )}
                </Dialog>
            </Container>
        </ThemeProvider>
    );
}
